import React from 'react'

import {
  BUYBACK_FIELD,
  BuybackDebtItem,
  BuybackPayoutInput,
  BuybackProcessCostItem,
} from '../../common/buybackTypes'
import { Money } from '../components/Money/Money'

export const COLOR_SCHEMES = {
  red: {
    payout: '#fecdd3', // rose-200
    commission: '#fda4af', // rose-300
    debtFee: '#fb7185', // rose-400
    debtAmount: '#fecaca', // red-200
    processCost: '#fca5a5', // red-300
  },
  orange: {
    payout: '#fed7aa', // orange-200
    commission: '#fdba74', // orange-300
    debtFee: '#fde047', // yellow-300
    debtAmount: '#fef08a', // yellow-200
    processCost: '#fef3c7', // yellow-100
  },
  green: {
    payout: '#d9f99d', // lime-200
    commission: '#bef264', // lime-300
    debtFee: '#a7f3d0', // emerald-200
    debtAmount: '#6ee7b7', // emerald-300
    processCost: '#86efac', // green-300
  },
} as const

/**
 * Format currency amount to human-readable string with k/mil abbreviations.
 */
export const formatChartCurrency = (value: number): string => {
  if (value === 0) return '0'
  if (Math.abs(value) < 1000000) {
    return `${(value / 1000).toFixed(0)} tis.`
  }
  return `${(value / 1000000).toFixed(1).replace('.', ',')} mil.`
}

/**
 * Render price with Money component for tables.
 */
export const payoutPriceRenderer = (amount: number) => (
  <Money symbol="Kč" grayscale decimalNumbers={0} amount={amount || 0} />
)

/**
 * Compute the payout amount (final cash flow to the seller).
 *  - OfferPrice - (Commission + DebtFees + ProcessCosts + DebtAmounts)
 *
 * Edge cases:
 * - Missing numeric fields are treated as 0.
 * - Negative intermediate values are prevented by using fallback 0; result itself can be negative.
 *
 * @param BuybackPayoutInput subset of `BuybackFormValues`
 * @returns Payout amount (can be negative).
 * @description OfferPrice - (Commission + DebtFees + ProcessCosts + DebtAmounts)
 */
export const computePayoutAmount = (formValues: BuybackPayoutInput): number => {
  const commission =
    typeof formValues[BUYBACK_FIELD.CommissionAmount] === 'number'
      ? formValues[BUYBACK_FIELD.CommissionAmount]
      : formValues[BUYBACK_FIELD.BaseCommissionAmount] ?? 0

  const debtsTotal = formValues[BUYBACK_FIELD.DebtAmountTotal] ?? 0
  const debtFees =
    typeof formValues[BUYBACK_FIELD.DebtFeeTotal] === 'number'
      ? formValues[BUYBACK_FIELD.DebtFeeTotal]
      : formValues[BUYBACK_FIELD.BaseDebtFeeTotal] ?? 0
  const processCosts = formValues[BUYBACK_FIELD.ProcessCostAmountTotal] ?? 0
  const offerPrice = formValues[BUYBACK_FIELD.OfferPrice] ?? 0

  const totalExpenses = commission + debtFees + processCosts + debtsTotal

  return offerPrice - totalExpenses
}

/**
 * Transform debt items to chart/table data format.
 */
export const transformDebtsToData = (
  debts: BuybackDebtItem[]
): Array<{
  key: string
  name: string
  amount: number
  fee: number
  secured: boolean
}> => {
  return debts.map((d, i) => ({
    key: d[BUYBACK_FIELD.DebtId] ?? String(i),
    name: d[BUYBACK_FIELD.DebtName] ?? 'Závazek',
    amount: Number(d[BUYBACK_FIELD.DebtAmount] ?? 0),
    fee: Number(d[BUYBACK_FIELD.DebtFee] ?? 0),
    secured: Boolean(d[BUYBACK_FIELD.Secured]),
  }))
}

/**
 * Transform process cost items to chart/table data format.
 */
export const transformProcessCostsToData = (
  processCosts: BuybackProcessCostItem[]
): Array<{
  key: string
  name: string
  amount: number
}> => {
  return processCosts.map((p, i) => ({
    key: p[BUYBACK_FIELD.ProcessCostId] ?? String(i),
    name: p[BUYBACK_FIELD.ProcessCostName] ?? 'Poplatek',
    amount: Number(p[BUYBACK_FIELD.ProcessCostAmount] ?? 0),
  }))
}
